import { Badge, Button, DataTable, Modal, ResultsCard, ResultsToolbar } from 'shared/components/ui';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Poll, Search, Clear, UnfoldMore, Visibility } from '@mui/icons-material';

import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import { Toast } from '../../../shared/components/Toast';
import { encuestasAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';
import './GrillaEncuestas.css';

const PAGE_SIZE = 50;
const COLORS_DEFAULT = ['#16a34a', '#4ade80', '#a3e635', '#facc15', '#fb923c', '#ef4444', '#b91c1c', '#7f1d1d'];

function getColorForValue(name) {
  if (!name) return null;
  const v = name.toLowerCase();
  // Calificaciones de atención
  if (v.includes('excelente')) return '#15803d';
  if (v.includes('muy buen')) return '#16a34a';
  if (v.includes('buen')) return '#4ade80';
  if (v.includes('regular')) return '#fb923c';
  if (v.includes('mal')) return '#dc2626';
  // Sí/No
  if (v === 'sí' || v === 'si') return '#16a34a';
  if (v === 'no') return '#dc2626';
  if (v.includes('tal vez')) return '#facc15';
  // Tiempos de espera (más corto = verde, más largo = rojo)
  if (v.includes('mismo día') || v.includes('un día') || v.includes('inmediato')) return '#15803d';
  if (v.includes('más de cinco') || v.includes('más de 5')) return '#dc2626';
  if (v.includes('menos de cinco') || v.includes('menos de 5')) return '#facc15';
  if (v.includes('cinco días') || v.includes('cinco dia') || v.includes('5 día')) return '#dc2626';
  if (v.includes('dos') || v.includes('tres') || v.includes('pocos')) return '#4ade80';
  if (v.includes('una semana') || v.includes('cuatro') || v.includes('cinco')) return '#facc15';
  if (v.includes('dos semanas') || v.includes('quince')) return '#fb923c';
  if (v.includes('más de') || v.includes('un mes') || v.includes('mucho')) return '#dc2626';
  // Sedes - gama verde a rojo distribuida
  if (v.includes('recoleta') && v.includes('plus')) return '#16a34a';
  if (v.includes('recoleta')) return '#4ade80';
  if (v.includes('lobos')) return '#a3e635';
  if (v.includes('domicilio')) return '#facc15';
  // Cómo conoció - gama verde a rojo
  if (v.includes('derivado') || v.includes('obra social')) return '#15803d';
  if (v.includes('recomendación') || v.includes('familiar') || v.includes('amigo')) return '#4ade80';
  if (v.includes('internet') || v.includes('google')) return '#a3e635';
  if (v.includes('redes') || v.includes('instagram') || v.includes('facebook')) return '#facc15';
  return null;
}

const GrillaEncuestas = () => {
  const [respuestas, setRespuestas] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEncuestas, setTotalEncuestas] = useState(0);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [tablaPage, setTablaPage] = useState(1);
  const [resumen, setResumen] = useState('');
  const [resumenLoading, setResumenLoading] = useState(false);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(respuestas);

  const cargar = useCallback(async (pagina = 1) => {
    setLoading(true);
    try {
      const res = await encuestasAPI.getRespuestas(
        pagina,
        PAGE_SIZE,
        fechaDesde || undefined,
        fechaHasta || undefined
      );
      const data = res.data;
      const qs = data?.questions || [];
      const submissions = data?.submissions || [];

      setQuestions(qs);
      setTotalEncuestas(data?.totalNumberOfSubmissionsPerFilter?.completed || submissions.length);
      setRespuestas(submissions.map(s => {
        // Extraer token del campo hidden (encuesta)
        const hiddenQuestion = qs.find(q => q.type === 'HIDDEN_FIELDS');
        const hiddenResponse = hiddenQuestion ? (s.responses || []).find(r => r.questionId === hiddenQuestion.id) : null;
        const tokenEncuesta = hiddenResponse?.answer?.encuesta || '';

        const campos = (s.responses || [])
          .map(r => {
            const question = qs.find(q => q.id === r.questionId);
            if (!question || question.type === 'HIDDEN_FIELDS') return null;
            const answer = Array.isArray(r.answer) ? r.answer.join(', ') : (typeof r.answer === 'object' ? JSON.stringify(r.answer) : r.answer);
            return {
              label: question.title || question.id,
              value: answer || '-',
              questionId: r.questionId
            };
          })
          .filter(Boolean);

        return { id: s.id, fecha: s.submittedAt ? new Date(s.submittedAt).toLocaleString('es-AR') : '', campos, tokenEncuesta };
      }));
      setTotalPages(data?.totalNumberOfPages || 1);
      setPage(pagina);

      // Desofuscar tokens para vincular con turnos
      const subs = submissions.map(s => {
        const hiddenQuestion = qs.find(q => q.type === 'HIDDEN_FIELDS');
        const hiddenResponse = hiddenQuestion ? (s.responses || []).find(r => r.questionId === hiddenQuestion.id) : null;
        return { id: s.id, token: hiddenResponse?.answer?.encuesta || '' };
      }).filter(s => s.token);

      // Desofuscar en paralelo
      const turnosMap = {};
      await Promise.all(subs.map(async (s) => {
        try {
          const res = await encuestasAPI.desofuscar(s.token);
          turnosMap[s.id] = res.data.idTurno;
        } catch { /* token inválido, ignorar */ }
      }));

      setRespuestas(prev => prev.map(r => ({
        ...r,
        idTurno: turnosMap[r.id] || null
      })));
    } catch {
      setToast({ message: 'Error al cargar las encuestas.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarResumen = async (regenerar = false) => {
    setResumenLoading(true);
    try {
      const res = await encuestasAPI.getResumen(regenerar);
      setResumen(res.data?.resumen || '');
    } catch {
      setResumen('');
    } finally {
      setResumenLoading(false);
    }
  };

  useEffect(() => { cargarResumen(); }, []);

  // Generar datos para gráficos
  const chartData = useMemo(() => {
    if (!respuestas.length || !questions.length) return {};

    const resultado = {};
    const preguntasGrafico = questions.filter(q => q.type === 'MULTIPLE_CHOICE' && q.title);

    preguntasGrafico.forEach(q => {
      const conteo = {};
      respuestas.forEach(r => {
        const campo = r.campos.find(c => c.questionId === q.id);
        if (campo && campo.value && campo.value !== '-') {
          conteo[campo.value] = (conteo[campo.value] || 0) + 1;
        }
      });
      resultado[q.id] = {
        title: q.title,
        data: Object.entries(conteo).map(([name, value]) => ({ name, value }))
      };
    });

    return resultado;
  }, [respuestas, questions]);

  const handleLimpiar = () => {
    setFechaDesde('');
    setFechaHasta('');
    setPage(1);
  };

  // Ordenar respuestas: peores primero
  const respuestasOrdenadas = useMemo(() => {
    const puntaje = (valor) => {
      if (!valor) return 3;
      const v = valor.toLowerCase();
      if (v.includes('mal')) return 1;
      if (v.includes('regular')) return 2;
      if (v.includes('no') && !v.includes('conoció')) return 2;
      if (v.includes('buen') && !v.includes('muy')) return 3;
      if (v.includes('muy buen')) return 4;
      if (v.includes('excelente')) return 5;
      if (v === 'sí' || v === 'si') return 5;
      return 3;
    };

    return [...sortedData].sort((a, b) => {
      const adminA = a.campos.find(c => c.label?.includes('administrativo'))?.value;
      const adminB = b.campos.find(c => c.label?.includes('administrativo'))?.value;
      const medA = a.campos.find(c => c.label?.includes('profesionales'))?.value;
      const medB = b.campos.find(c => c.label?.includes('profesionales'))?.value;
      const recA = a.campos.find(c => c.label?.includes('Recomendaría'))?.value;
      const recB = b.campos.find(c => c.label?.includes('Recomendaría'))?.value;

      const scoreA = puntaje(adminA) + puntaje(medA) + puntaje(recA);
      const scoreB = puntaje(adminB) + puntaje(medB) + puntaje(recB);
      return scoreA - scoreB;
    });
  }, [sortedData]);

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <Poll sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Encuestas de Satisfacción
          </h2>
          <span className="page-subtitle">{totalEncuestas} respuestas recibidas</span>
        </div>

        <div className="filters-card">
          <div className="filters-header">
            <h3 className="filters-title">Filtros</h3>
          </div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${fechaDesde ? 'filter-active' : ''}`}>
                <label className="filter-label">Desde</label>
                <input type="date" className="filter-input" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
              </div>
              <div className={`filter-group ${fechaHasta ? 'filter-active' : ''}`}>
                <label className="filter-label">Hasta</label>
                <input type="date" className="filter-input" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => cargar(1)} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={handleLimpiar}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Resumen IA */}
        <ResultsCard className="results-card" style={{ marginBottom: '1.5rem' }}>
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">📊 Resumen IA</h3>
            </div>
            <div className="results-info">
              <Button variant="outline-primary" size="sm" onClick={() => cargarResumen(true)} disabled={resumenLoading}>
                {resumenLoading ? 'Generando...' : 'Regenerar'}
              </Button>
            </div>
          </ResultsToolbar>
          <div style={{ padding: '1.5rem' }}>
            {resumenLoading && <p style={{ color: '#64748b' }}>Generando resumen con IA...</p>}
            {!resumenLoading && resumen && (
              <div className="resumen-ia-content" style={{ fontSize: '0.9rem', lineHeight: 1.7, color: '#374151' }} dangerouslySetInnerHTML={{ __html: resumen
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/^([A-ZÁÉÍÓÚÑ\s]{4,})$/gm, '<div style="font-size:0.95rem;font-weight:600;color:#1e293b;margin-top:1.2rem;margin-bottom:0.4rem;padding-bottom:0.3rem;border-bottom:1px solid #e5e7eb">$1</div>')
                .replace(/\n- /g, '<br/>• ')
                .replace(/\n/g, '<br/>')
              }} />
            )}
            {!resumenLoading && !resumen && <p style={{ color: '#94a3b8' }}>No hay resumen disponible. Presioná "Regenerar" para generar uno.</p>}
          </div>
        </ResultsCard>

        {/* Dashboard de gráficos */}
        {Object.keys(chartData).length > 0 && (
          <ResultsCard className="results-card" style={{ marginBottom: '1.5rem' }}>
            <ResultsToolbar className="results-header">
              <h3 className="results-title">Resumen Visual</h3>
            </ResultsToolbar>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem', padding: '1.5rem' }}>
              {Object.values(chartData).map((chart, idx) => (
                <div key={idx} style={{ minHeight: 300, overflow: 'visible' }}>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '1rem', color: '#374151', lineHeight: 1.4 }}>
                    {chart.title}
                  </h4>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={chart.data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={true}>
                        {chart.data.map((entry, i) => <Cell key={i} fill={getColorForValue(entry.name) || COLORS_DEFAULT[i % COLORS_DEFAULT.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: '0.72rem', paddingTop: '0.5rem' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          </ResultsCard>
        )}

        {/* Tabla de respuestas */}
        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">Detalle de Respuestas</h3>
            </div>
            <div className="results-info">
              <span className="results-count">{respuestas.length} encuestas</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('fecha')} style={{ cursor: 'pointer' }}>
                    Fecha {getSortIcon('fecha') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Turno</th>
                  <th>Centro</th>
                  <th>Atención Admin.</th>
                  <th>Atención Médica</th>
                  <th>Recomendaría</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {respuestasOrdenadas.length === 0 ? (
                  <tr><td colSpan="7" className="empty-state">{loading ? 'Cargando...' : 'No hay encuestas para mostrar'}</td></tr>
                ) : (
                  respuestasOrdenadas.slice((tablaPage - 1) * PAGE_SIZE, tablaPage * PAGE_SIZE).map((r) => (
                    <tr key={r.id}>
                      <td>{r.fecha}</td>
                      <td>{r.idTurno ? <Badge bg="dark">#{r.idTurno}</Badge> : '-'}</td>
                      <td>{r.campos.find(c => c.label?.includes('Centro en el que se realizó'))?.value || '-'}</td>
                      <td>
                        <Badge bg={getBadgeColor(r.campos.find(c => c.label?.includes('personal administrativo'))?.value)}>
                          {r.campos.find(c => c.label?.includes('personal administrativo'))?.value || '-'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={getBadgeColor(r.campos.find(c => c.label?.includes('profesionales de NEUROSUEÑO'))?.value)}>
                          {r.campos.find(c => c.label?.includes('profesionales de NEUROSUEÑO'))?.value || '-'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={r.campos.find(c => c.label?.includes('Recomendaría'))?.value === 'Sí' ? 'success' : 'secondary'}>
                          {r.campos.find(c => c.label?.includes('Recomendaría'))?.value || '-'}
                        </Badge>
                      </td>
                      <td className="action-cell">
                        <Button variant="outline-primary" size="sm" onClick={() => setDetalle(r)}>
                          <Visibility sx={{ fontSize: 16, mr: 0.5 }} /> Ver
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>

          {respuestasOrdenadas.length > PAGE_SIZE && (() => {
            const tablaTotalPages = Math.ceil(respuestasOrdenadas.length / PAGE_SIZE);
            return (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
                <Button variant="outline-secondary" size="sm" onClick={() => setTablaPage(1)} disabled={tablaPage === 1}>«</Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setTablaPage(p => p - 1)} disabled={tablaPage === 1}>‹</Button>
                <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {tablaPage} de {tablaTotalPages}</span>
                <Button variant="outline-secondary" size="sm" onClick={() => setTablaPage(p => p + 1)} disabled={tablaPage === tablaTotalPages}>›</Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setTablaPage(tablaTotalPages)} disabled={tablaPage === tablaTotalPages}>»</Button>
              </div>
            );
          })()}

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => cargar(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => cargar(page - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => cargar(page + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => cargar(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />

      {/* Modal de detalle */}
      <Modal show={detalle !== null} onHide={() => setDetalle(null)} size="xl" centered dialogClassName="modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>Detalle de Encuesta</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem', maxHeight: '70vh', overflowY: 'auto' }}>
          {detalle && (
            <div>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Respondida el {detalle.fecha} {detalle.idTurno && <> · Turno <strong>#{detalle.idTurno}</strong></>}
              </p>
              <DataTable className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>Pregunta</th>
                    <th>Respuesta</th>
                  </tr>
                </thead>
                <tbody>
                  {detalle.campos.map((campo, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 500 }}>{campo.label}</td>
                      <td>{campo.value}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetalle(null)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

function getBadgeColor(value) {
  if (!value) return 'secondary';
  const v = value.toLowerCase();
  if (v.includes('excelente')) return 'success';
  if (v.includes('muy buen')) return 'success';
  if (v.includes('buen')) return 'warning';
  if (v.includes('regular')) return 'warning';
  if (v.includes('mal')) return 'danger';
  return 'secondary';
}

export default GrillaEncuestas;
